import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CloudUpload,
  PictureAsPdf,
  CheckCircle,
  Warning,
  Info,
  Refresh,
  Storefront,
} from '@mui/icons-material';
import api, { lookupAPI } from '../services/api';
import { Store } from '../types';

const BILLS_PER_DAY_OPTIONS = [
  { value: '3', label: '3 Sample Bills (Total)' },
  { value: '20-30', label: '20-30 per day' },
  { value: '40-50', label: '40-50 per day' },
  { value: '60-70', label: '60-70 per day' },
  { value: '100-150', label: '100-150 per day' },
  { value: 'full', label: 'Full (All Bills)' },
  { value: 'h1', label: 'H1 Report (Schedule H1 Only)' },
];

interface AuditItem {
  medicineName: string;
  quantity: string;
  batch: string;
  expiry: string;
  scheduleType?: string;
  mobileNo?: string;
}

interface AuditBill {
  billNo: string;
  date: string;
  patientName: string;
  doctorName: string;
  mobileNo?: string;
  items: AuditItem[];
}

interface AuditData {
  totalRowsInCsv: number;
  filteredItemsCount: number;
  selectedBillsCount: number;
  selectedItemsCount: number;
  dateRange: {
    startDate: string | null;
    endDate: string | null;
  };
  billsPerDay: string;
  bills: AuditBill[];
}

interface UploadResponse {
  success: boolean;
  data: AuditData;
  message: string;
}

// First and last day of the previous month, as YYYY-MM-DD (local time)
const lastMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(first), to: iso(last) };
};

const PharmacistAudit: React.FC = () => {
  const [source, setSource] = useState<'pos' | 'csv'>('pos');
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState<number | ''>('');
  const [fromDate, setFromDate] = useState<string>(lastMonthRange().from);
  const [toDate, setToDate] = useState<string>(lastMonthRange().to);
  const [file, setFile] = useState<File | null>(null);
  const [billsPerDay, setBillsPerDay] = useState<string>('40-50');
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditData, setAuditData] = useState<AuditData | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const handleLoadFromPos = async () => {
    if (!storeId) {
      setError('Please select a store');
      return;
    }
    if (!fromDate || !toDate || fromDate > toDate) {
      setError('Please choose a valid From and To date');
      return;
    }
    setUploading(true);
    setError(null);
    setMessage(null);
    setAuditData(null);
    try {
      const response = await api.post<UploadResponse>('/utilities/pharmacist-audit/from-pos', {
        storeId, fromDate, toDate, billsPerDay,
      });
      if (response.data.success) {
        setAuditData(response.data.data);
        setMessage(response.data.message);
      } else {
        setError('Failed to load bills from POS');
      }
    } catch (err: any) {
      console.error('Error loading POS bills:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load bills from POS');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];

      // Validate file type
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }

      setFile(selectedFile);
      setError(null);
      setAuditData(null);
      setMessage(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setUploading(true);
    setError(null);
    setMessage(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('billsPerDay', billsPerDay);

    try {
      const response = await api.post<UploadResponse>('/utilities/pharmacist-audit/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setAuditData(response.data.data);
        setMessage(response.data.message);
      } else {
        setError('Failed to process CSV file');
      }
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to upload and process CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!auditData) {
      setError('No audit data available');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await api.post('/utilities/pharmacist-audit/generate-pdf', {
        bills: auditData.bills,
        dateRange: auditData.dateRange,
        billsPerDay: auditData.billsPerDay || billsPerDay,
      }, {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const isH1 = auditData?.billsPerDay === 'h1';
      link.download = isH1
        ? `H1_Audit_Report_${new Date().toISOString().split('T')[0]}.pdf`
        : `Pharmacist_Audit_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF report');
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setAuditData(null);
    setError(null);
    setMessage(null);
    // Reset the file input
    const fileInput = document.getElementById('audit-csv-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Upload Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {source === 'pos' ? <Storefront color="primary" /> : <CloudUpload color="primary" />}
              {source === 'pos' ? 'Load Bills from POS' : 'Upload Audit CSV File'}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={source}
              onChange={(_, v) => { if (v) { setSource(v); handleReset(); } }}
              disabled={uploading}
            >
              <ToggleButton value="pos">From POS</ToggleButton>
              <ToggleButton value="csv">Upload CSV</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {source === 'pos'
              ? 'Pick a store and date range. Bills are read directly from the POS (including the evitalRx history imported into it), so no export is needed. '
              : 'Upload the Pharmacist Audit Report CSV file exported from evitalRx. '}
            The system will filter for Drug, Nutraceuticals, and Generic items, validate data quality,
            and select bills based on the chosen "Bills Per Day" filter.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {source === 'pos' && (
              <>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="audit-store-label">Store</InputLabel>
                  <Select
                    labelId="audit-store-label"
                    value={storeId}
                    label="Store"
                    onChange={(e) => setStoreId(e.target.value as number)}
                    disabled={uploading}
                  >
                    {stores.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={uploading}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={uploading}
                />
              </>
            )}

            {source === 'csv' && (<>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="audit-csv-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="audit-csv-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                disabled={uploading}
              >
                Choose CSV File
              </Button>
            </label>

            {file && (
              <Chip
                label={file.name}
                onDelete={handleReset}
                color="primary"
                variant="outlined"
              />
            )}
            </>)}

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="bills-per-day-label">Bills Per Day</InputLabel>
              <Select
                labelId="bills-per-day-label"
                value={billsPerDay}
                label="Bills Per Day"
                onChange={(e) => setBillsPerDay(e.target.value)}
                disabled={uploading}
              >
                {BILLS_PER_DAY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={source === 'pos' ? handleLoadFromPos : handleUpload}
              disabled={(source === 'pos' ? !storeId : !file) || uploading}
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            >
              {uploading ? 'Processing...' : source === 'pos' ? 'Load & Preview' : 'Upload & Preview'}
            </Button>

            {auditData && (
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<Refresh />}
                color="secondary"
              >
                Start Over
              </Button>
            )}
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                {source === 'pos' ? 'Loading bills from POS...' : 'Processing CSV file...'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Message Alert */}
      {message && (
        <Alert
          severity={auditData?.bills.length === 0 ? 'warning' : 'success'}
          sx={{ mb: 3 }}
          icon={auditData?.bills.length === 0 ? <Warning /> : <CheckCircle />}
        >
          {message}
        </Alert>
      )}

      {/* Statistics Section */}
      {auditData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="info" />
              Processing Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                  <Typography variant="h4" color="primary">
                    {auditData.totalRowsInCsv}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {source === 'pos' ? 'Total Bill Lines' : 'Total CSV Rows'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                  <Typography variant="h4" color="secondary">
                    {auditData.filteredItemsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Valid Items (Drug/Generic)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: auditData.selectedBillsCount > 0 ? 'success.light' : 'warning.light' }}>
                  <Typography variant="h4" color={auditData.selectedBillsCount > 0 ? 'success.dark' : 'warning.dark'}>
                    {auditData.selectedBillsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Selected Bills
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                  <Typography variant="h4" color="info.dark">
                    {auditData.selectedItemsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Items in Report
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {auditData.dateRange.startDate && auditData.dateRange.endDate && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Date Range:</strong> {auditData.dateRange.startDate} to {auditData.dateRange.endDate}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Section */}
      {auditData && auditData.bills.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdf color="error" />
                Preview - Selected Bills
              </Typography>
              <Button
                variant="contained"
                color="error"
                onClick={handleGeneratePDF}
                disabled={generating}
                startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
              >
                {generating ? 'Generating...' : 'Generate PDF Report'}
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Below are the {auditData.bills.length} bill(s) that will be included in the audit report.
              Review the data and click "Generate PDF Report" to download.
            </Typography>

            {auditData.bills.map((bill, billIndex) => (
              <Box key={bill.billNo} sx={{ mb: 3 }}>
                {billIndex > 0 && <Divider sx={{ mb: 2 }} />}

                <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip label={`Bill #${bill.billNo}`} color="primary" size="small" />
                  <Typography variant="body2">
                    <strong>Date:</strong> {bill.date}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Patient:</strong> {bill.patientName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Doctor:</strong> {bill.doctorName}
                  </Typography>
                  {auditData.billsPerDay === 'h1' && bill.mobileNo && (
                    <Typography variant="body2">
                      <strong>Mobile:</strong> {bill.mobileNo}
                    </Typography>
                  )}
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Medicine Name</strong></TableCell>
                        <TableCell align="center"><strong>Qty</strong></TableCell>
                        <TableCell><strong>Batch</strong></TableCell>
                        <TableCell><strong>Expiry</strong></TableCell>
                        {auditData.billsPerDay === 'h1' && (
                          <>
                            <TableCell><strong>Schedule</strong></TableCell>
                            <TableCell><strong>Mobile No.</strong></TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bill.items.map((item, itemIndex) => (
                        <TableRow key={itemIndex} hover>
                          <TableCell>{item.medicineName}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell>{item.batch}</TableCell>
                          <TableCell>{item.expiry}</TableCell>
                          {auditData.billsPerDay === 'h1' && (
                            <>
                              <TableCell>{item.scheduleType}</TableCell>
                              <TableCell>{item.mobileNo}</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty State - No good bills found */}
      {auditData && auditData.bills.length === 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Warning sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Quality Bills Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 500, mx: 'auto' }}>
                {source === 'pos' ? 'No bills in this store and date range' : 'The uploaded CSV did not contain any bills'} that meet all quality criteria:
              </Typography>
              <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Valid patient name (not "Counter Sale", "Walk-in" or "Coverup Bill")</li>
                  <li>Valid doctor name (non-empty)</li>
                  <li>Valid expiry date (MM/YY format)</li>
                  <li>Valid batch number (non-empty)</li>
                </Typography>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleGeneratePDF}
                  disabled={generating}
                  startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
                >
                  {generating ? 'Generating...' : 'Download Empty Report (with note)'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Instructions
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            <ol style={{ paddingLeft: '20px', margin: 0 }}>
              <li><strong>From POS</strong> (default): pick the store and date range and click "Load &amp; Preview".
                Works for all stores, including dates before the POS switch (the evitalRx history is imported).</li>
              <li><strong>Upload CSV</strong>: export the Pharmacist Audit Report CSV from evitalRx and upload it</li>
              <li>The system will automatically:
                <ul>
                  <li>Filter for Drug, Nutraceuticals, and Generic items only (excluding OTC)</li>
                  <li>Clean patient names (remove numbers) and skip counter / walk-in bills</li>
                  <li>Validate doctor names, expiry dates (MM/YY), and batch numbers</li>
                  <li>Select bills based on the chosen "Bills Per Day" filter</li>
                </ul>
              </li>
              <li>Review the preview and click "Generate PDF Report" to download</li>
              <li>The PDF is in landscape A4 format, matching the manual ledger layout</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PharmacistAudit;
